import { html, TemplateResult, css, unsafeCSS } from 'lit';
import { property, state } from 'lit/decorators.js';
import { DERModuleElement, AnalyticsService, AssetService } from '@dertour/common-lib';
import { Task } from '@lit/task';
import { renderIntro } from './templates/intro';
import { ImpressionsList } from './types/types';
import { ImpressionsService } from './services/impressions.service';
import mainStyles from './styles/main.scss?inline';
import swiperCss from 'swiper/css?inline';
import swiperNavCss from 'swiper/css/navigation?inline';
import './components/slider-wrapper';
import { localized } from '@lit/localize';
import { updateLocale } from './localization/config';

// Export AssetService for demo usage
export { AssetService };

/**
 * DERTOUR Impressions Module
 */
@localized()
export class Impressions extends DERModuleElement {
    static override styles = [
        css`
            ${unsafeCSS(mainStyles)}
        `,
        css`
            ${unsafeCSS(swiperCss)}
        `,
        css`
            ${unsafeCSS(swiperNavCss)}
        `,
    ];

    @property({ attribute: 'identifier' })
    identifier = '';

    @property({ attribute: 'text-position' })
    textPosition: 'left' | 'right' = 'left';

    @property({ attribute: 'ab-variant' })
    abVariant: 'a' | 'b' = 'a';

    @property({ attribute: 'intent' })
    intent?: string;

    @state()
    impressionsList: ImpressionsList | undefined;

    private _contentTask: Task<readonly [string, string, string, string, string, string], ImpressionsList> | null =
        null;

    /**
     * Initialize the component
     */
    async connectedCallback(): Promise<void> {
        super.connectedCallback();

        // 1. Set locale based on lang attribute (wait for it to load)
        const langAttribute = this.lang ?? 'de';
        await updateLocale(langAttribute);

        // Listen for analytics events from slider-wrapper and forward them
        this.addEventListener('der-analytics', (event: Event) => {
            const customEvent = event as CustomEvent;
            // Only forward events that come from child elements, not from ourselves
            if (customEvent.target !== this) {
                customEvent.stopPropagation();

                // Forward the analytics event using AnalyticsService
                AnalyticsService.dispatch(this, customEvent.detail);
            }
        });

        this._contentTask = new Task<readonly [string, string, string, string, string, string], ImpressionsList>(
            this,
            async ([brand, tenant, currency, lang, identifier, intent]) => {
                console.log('-------------------------------');
                let data: ImpressionsList;
                try {
                    data = await ImpressionsService.fetchImpressions({
                        brand,
                        tenant,
                        currency,
                        lang,
                        identifier,
                        intent,
                    });
                } catch (error) {
                    console.error('[Impressions] Failed to fetch impressions', error);
                    throw error;
                } finally {
                    /**
                     * Unsets the skeleton set in der-tm-ui-library vars.css
                     */
                    this.setAttribute('loaded', 'true');
                }

                return data;
            },
            () =>
                [
                    this.brand,
                    this.tenant,
                    this.currency,
                    this.lang ?? 'de-CH',
                    this.identifier,
                    this.intent ?? '',
                ] as const
        );
    }

    /**
     * Update lifecycle - watch for brand and language changes at runtime
     * @param changedProperties - Map of changed properties with their previous values
     */
    updated(changedProperties: Map<string, unknown>): void {
        super.updated(changedProperties);

        // Handle language changes at runtime
        if (changedProperties.has('lang')) {
            const newLang = this.lang ?? 'de';
            console.log(`Language changed to: ${newLang}`);
            updateLocale(newLang).then(() => {
                this.requestUpdate();
            });
        }
    }

    /**
     * Render the impressions module
     * @returns Template result
     */
    protected render(): TemplateResult {
        if (!this._contentTask) {
            return html``;
        }

        return (
            this._contentTask.render({
                pending: () => html``,
                complete: (impressionsList: ImpressionsList) => html`
                    <section class="der-tm-impressions-module">
                        ${renderIntro(impressionsList)}
                        <der-tm-impressions-slider-wrapper
                            .impressionsList=${impressionsList}
                            .textPosition=${this.textPosition}
                            .abVariant=${this.abVariant}
                        ></der-tm-impressions-slider-wrapper>
                    </section>
                `,
                error: () => html``,
            }) ?? html``
        );
    }
}

if (!customElements.get('der-tm-impressions')) {
    customElements.define('der-tm-impressions', Impressions);
}
